<template>
    <div>
        <jet-form-card @submitted="submit">
            <template #form>
                <div class="col-span-6">
                    <jet-label for="title" value="Title"/>
                    <jet-input id="title" type="text" class="mt-1 block w-full" v-model="form.title" required
                               :error="!!form.errors.title"
                               autofocus
                               @blur="handleSlug"/>
                    <jet-input-error :message="form.errors.title"/>
                </div>
                <div class="col-span-6">
                    <jet-label for="slug" value="Slug"/>
                    <jet-input id="slug" type="text" class="mt-1 block w-full" v-model="form.slug" required
                               :error="!!form.errors.slug"
                               autofocus
                               @input="customSlug = true"/>
                    <jet-input-error :message="form.errors.slug"/>
                </div>
                <div class="col-span-6">
                    <jet-label for="button_label" value="Button Label"/>
                    <jet-input id="button_label" type="text" class="mt-1 block w-full" v-model="form.button_label"
                               required
                               :error="!!form.errors.button_label"
                               autofocus/>
                    <jet-input-error :message="form.errors.button_label"/>
                </div>
                <div class="col-span-6">
                    <input type="file"
                           class="hidden"
                           ref="photoInput"
                           accept="image/*"
                           @change="updatePhotoPreview">

                    <jet-label for="image" value="Image"/>

                    <div class="mt-2" v-if="photoPreview">
                        <img :src="photoPreview" :alt="post?.title" class="sm:rounded-md h-80 w-160 object-cover">
                    </div>

                    <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="selectNewPhoto">
                        Select A New Image
                    </jet-secondary-button>

                    <jet-secondary-button type="button" class="mt-2"
                                          v-if="photoPreview" @click.prevent="deletePhoto">
                        Remove Image
                    </jet-secondary-button>

                    <jet-input-error :message="form.errors.image" class="mt-2"/>
                </div>
                <div class="col-span-6">
                    <jet-label for="description" value="Description"/>
                    <jet-input id="description" type="text" class="mt-1 block w-full" v-model="form.description"
                               :error="!!form.errors.description"
                               autofocus/>
                    <jet-input-error :message="form.errors.description"/>
                </div>
                <!--<div class="col-span-6">
                    <jet-label for="text" value="Text"/>
                    <jet-input id="text" type="text" class="mt-1 block w-full" v-model="form.text"
                               :error="!!form.errors.text"
                               autofocus/>
                    <jet-input-error :message="form.errors.description"/>
                </div>-->
                <div class="col-span-6">
                    <jet-label for="external_url" value="External URL"/>
                    <jet-input id="external_url" type="text" class="mt-1 block w-full" v-model="form.external_url"
                               :error="!!form.errors.external_url"
                               autofocus/>
                    <jet-input-error :message="form.errors.external_url"/>
                </div>
                <div class="col-span-6">
                    <jet-label for="name" value="Tags"/>
                    <tags-input v-model="form.tags" :all-tags="allTags"></tags-input>
                </div>
            </template>
            <template #actions>
                <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                    Saved.
                </jet-action-message>
                <jet-button :class="{ 'opacity-25': form.processing }"
                            :disabled="form.processing">
                    Save
                </jet-button>
            </template>
        </jet-form-card>
    </div>
</template>

<script>
import JetFormCard from '@/Jetstream/FormCard';
import JetActionMessage from '@/Jetstream/ActionMessage.vue';
import JetInputError from '@/Jetstream/InputError';
import JetInput from '@/Jetstream/Input';
import JetButton from '@/Jetstream/Button';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetLabel from '@/Jetstream/Label';
import {useForm} from "@inertiajs/inertia-vue3";
import TagsInput from "@/Components/TagsInput";
import {ref} from 'vue';
import slugify from '@/utils/slugify';

export default {
    name: "PostForm",
    components: {
        TagsInput,
        JetInput,
        JetLabel,
        JetButton,
        JetSecondaryButton,
        JetFormCard,
        JetActionMessage,
        JetInputError,
    },
    props: {
        allTags: {
            type: Array,
            default: [],
        },
        post: {
            type: Object,
            required: false,
        }
    },
    setup(props) {
        const formData = {
            title: props.post?.title,
            slug: props.post?.slug,
            button_label: props.post?.button_label,
            description: props.post?.description,
            image: props.post?.image,
            // text: props.post?.text,
            external_url: props.post?.external_url,
            tags: props.post?.tags.map((tag) => tag.name.en) ?? [],
        };

        let form = null;
        if (!props.post) {
            form = useForm(formData);
        } else {
            form = useForm({
                _method: 'PUT',
                ...formData,
            });
        }

        let photo = props.post?.image;
        const photoPreview = ref(props.post?.image_url);
        const photoInput = ref(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            photoPreview.value = e.target.result;
        };

        const submit = () => {
            if (photo === null || photo instanceof File) {
                form.image = photo;
            } else {
                delete form.image;
            }

            if (!props.post) {
                form.post(route('admin.post.store'));
            } else {
                form.post(route('admin.post.update', props.post.id));
            }
        }

        const customSlug = ref(Boolean(props.post));
        const handleSlug = () => {
            if (!customSlug.value) {
                form.slug = slugify(form.title);
            }
        };

        const selectNewPhoto = () => {
            photoInput.value.click();
        };

        const updatePhotoPreview = (event) => {
            photo = event.target.files[0];

            if (!photo) {
                return;
            }

            reader.readAsDataURL(photo);
        };

        const deletePhoto = () => {
            photo = null;
            photoPreview.value = null;
        };

        return {
            customSlug,
            form,
            photoInput,
            photoPreview,
            handleSlug,
            selectNewPhoto,
            updatePhotoPreview,
            deletePhoto,
            submit,
        };
    },
}
</script>

<style scoped>

</style>
